import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  subscribeToActivities,
  subscribeToPlantActivities,
  addActivity as addActivityService,
  updateActivity as updateActivityService,
  deleteActivity as deleteActivityService,
} from '../services/activities';
import type { Activity, CreateActivityInput, UpdateActivityInput } from '../types/activity';

interface UseActivitiesReturn {
  activities: Activity[];
  loading: boolean;
  error: string | null;
  addActivity: (input: CreateActivityInput) => Promise<{ success: boolean; error?: string }>;
  updateActivity: (activityId: string, input: UpdateActivityInput) => Promise<{ success: boolean; error?: string }>;
  deleteActivity: (activityId: string) => Promise<{ success: boolean; error?: string }>;
}

/**
 * Hook for all activities across all plants.
 */
export function useActivities(maxResults = 50): UseActivitiesReturn {
  const { user } = useAuth();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setActivities([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribeToActivities(
      user.uid,
      maxResults,
      (data) => {
        setActivities(data);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, maxResults]);

  const addActivity = useCallback(
    async (input: CreateActivityInput) => {
      if (!user) return { success: false, error: 'Not signed in' };
      const result = await addActivityService(user.uid, input);
      if (result.success) return { success: true };
      return { success: false, error: result.error };
    },
    [user]
  );

  const updateActivity = useCallback(
    async (activityId: string, input: UpdateActivityInput) => {
      if (!user) return { success: false, error: 'Not signed in' };
      const result = await updateActivityService(user.uid, activityId, input);
      if (result.success) return { success: true };
      return { success: false, error: result.error };
    },
    [user]
  );

  const deleteActivity = useCallback(
    async (activityId: string) => {
      if (!user) return { success: false, error: 'Not signed in' };
      const result = await deleteActivityService(user.uid, activityId);
      if (result.success) return { success: true };
      return { success: false, error: result.error };
    },
    [user]
  );

  return { activities, loading, error, addActivity, updateActivity, deleteActivity };
}

/**
 * Hook for activities for a specific plant.
 */
export function usePlantActivities(plantId: string | null, maxResults = 20): UseActivitiesReturn {
  const { user } = useAuth();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !plantId) {
      setActivities([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribeToPlantActivities(
      user.uid,
      plantId,
      maxResults,
      (data) => {
        setActivities(data);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, plantId, maxResults]);

  const addActivity = useCallback(
    async (input: CreateActivityInput) => {
      if (!user) return { success: false, error: 'Not signed in' };
      const result = await addActivityService(user.uid, input);
      if (result.success) return { success: true };
      return { success: false, error: result.error };
    },
    [user]
  );

  const updateActivity = useCallback(
    async (activityId: string, input: UpdateActivityInput) => {
      if (!user) return { success: false, error: 'Not signed in' };
      const result = await updateActivityService(user.uid, activityId, input);
      if (result.success) return { success: true };
      return { success: false, error: result.error };
    },
    [user]
  );

  const deleteActivity = useCallback(
    async (activityId: string) => {
      if (!user) return { success: false, error: 'Not signed in' };
      const result = await deleteActivityService(user.uid, activityId);
      if (result.success) return { success: true };
      return { success: false, error: result.error };
    },
    [user]
  );

  return { activities, loading, error, addActivity, updateActivity, deleteActivity };
}
